import { Box, Chip, Divider, Grid, Link, Typography } from '@mui/material'
import {
  DreamJobFormData,
  TIER_ORDER,
  formatCalendarDay,
  tierGroupLabels,
  urgencyLabels,
  workStyleLabels,
} from '../../utils/dreamJobForm'

interface DreamJobSummaryProps {
  data: DreamJobFormData
}

function Field({
  label,
  children,
  wide,
  empty,
}: {
  label: string
  children: React.ReactNode
  wide?: boolean
  empty: boolean
}) {
  return (
    <Grid size={{ xs: 12, md: wide ? 12 : 6 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      {empty ? (
        <Typography variant="body2" color="text.secondary">
          Not set
        </Typography>
      ) : (
        children
      )}
    </Grid>
  )
}

function Chips({ items, color }: { items: string[]; color?: 'error' }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items.map((item) => (
        <Chip key={item} label={item} size="small" variant="outlined" color={color} />
      ))}
    </Box>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  )
}

// The saved profile, read-only. Same sections and order as the edit form so a
// person finds each answer where they entered it.
export default function DreamJobSummary({ data }: DreamJobSummaryProps) {
  const companiesByTier = TIER_ORDER.map((tier) => ({
    tier,
    companies: data.targetCompanies.filter((c) => c.tier === tier),
  })).filter((group) => group.companies.length > 0)
  const startDate = formatCalendarDay(data.targetStartDate)

  return (
    <Box>
      <Section title="Target Position">
        <Field label="Target Role" empty={!data.targetRole}>
          <Typography variant="body1">{data.targetRole}</Typography>
        </Field>
        <Field label="Target Industry" empty={!data.targetIndustry}>
          <Typography variant="body1">{data.targetIndustry}</Typography>
        </Field>
        <Field label="Target Companies" wide empty={companiesByTier.length === 0}>
          {companiesByTier.map(({ tier, companies }) => (
            <Box key={tier} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {tierGroupLabels[tier].toUpperCase()} ({companies.length})
              </Typography>
              <Chips items={companies.map((c) => c.name)} />
            </Box>
          ))}
        </Field>
        <Field label="Target Job Links" wide empty={data.targetJobLinks.length === 0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {data.targetJobLinks.map((link) => (
              <Link key={link} href={link} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                {link}
              </Link>
            ))}
          </Box>
        </Field>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Section title="Compensation">
        <Field label="Target Salary" empty={!data.salaryTarget}>
          <Typography variant="body1">{data.salaryTarget}</Typography>
        </Field>
        <Field label="Minimum Acceptable Salary" empty={!data.salaryMinimum}>
          <Typography variant="body1">{data.salaryMinimum}</Typography>
        </Field>
        <Field label="Benefits Priorities" wide empty={data.benefitsPriorities.length === 0}>
          <Chips items={data.benefitsPriorities} />
        </Field>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Section title="Work Style & Location">
        <Field label="Preferred Work Style" empty={!data.workStyle}>
          <Typography variant="body1">{data.workStyle ? workStyleLabels[data.workStyle] : ''}</Typography>
        </Field>
        <Field label="Willing to Relocate" empty={false}>
          <Typography variant="body1">{data.willingToRelocate ? 'Yes' : 'No'}</Typography>
        </Field>
        <Field label="Location Preferences" wide empty={data.locationPreferences.length === 0}>
          <Chips items={data.locationPreferences} />
        </Field>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Section title="Priorities & Deal Breakers">
        <Field label="What's Most Important" wide empty={data.priorities.length === 0}>
          <Chips items={data.priorities} />
        </Field>
        <Field label="Deal Breakers" wide empty={data.dealBreakers.length === 0}>
          <Chips items={data.dealBreakers} color="error" />
        </Field>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Section title="Timeline">
        <Field label="Target Start Date" empty={!startDate}>
          <Typography variant="body1">{startDate}</Typography>
        </Field>
        <Field label="Urgency" empty={false}>
          <Typography variant="body1">{urgencyLabels[data.urgency]}</Typography>
        </Field>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Section title="Additional Requirements">
        <Field label="Notes" wide empty={!data.additionalRequirements}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {data.additionalRequirements}
          </Typography>
        </Field>
      </Section>
    </Box>
  )
}
